// Modules
import { useState, useEffect } from 'react';
import L from 'leaflet';
import { useMap } from 'react-leaflet';

// Leaflet plugin for address search
import 'leaflet-control-geocoder';
import 'leaflet-control-geocoder/dist/Control.Geocoder.css';

// Config:
import config from 'config/config';

function LeafletControlGeocoder({ currentTab, stateBounds, defaultMapZoom }) {
    const [mapGeoControl, setMapGeoControl] = useState(null);
    const [mapZoomControl, setMapZoomControl] = useState(null);

    const map = useMap();

    // // Effects // //
    useEffect(() => {
        // We are using Nominatim for geocoding which is the default with leaflet-control-geocoder
        const geocoder = L.Control.Geocoder.nominatim({
            // Params for Nominatim to limit results to two and only search within the US
            geocodingQueryParams: {
                limit: 2,
                countrycodes: 'us',
            },
        });

        const geocoderControl = L.Control.geocoder({
            query: '',
            placeholder: 'Search here...',
            defaultMarkGeocode: false,
            iconLabel: 'address-search',
            geocoder,
            collapsed: false,
        }).on('markgeocode', (e) => {
            const latlng = e.geocode.center;
            const marker = L.marker(latlng);

            // Create the button to delete the marker
            const btn = document.createElement('button');
            btn.innerText = 'Delete Marker';
            btn.className = 'address-search-popup-btn';
            btn.onclick = () => map.removeLayer(marker);

            // Create elements for popup content
            const contentDiv = document.createElement('div');
            contentDiv.className = 'address-search-popup';
            const buttonDiv = document.createElement('div');
            const content = document.createTextNode(e.geocode.name);
            buttonDiv.appendChild(btn);
            contentDiv.appendChild(content);
            contentDiv.appendChild(buttonDiv);
            const popup = L.popup().setContent(contentDiv);

            marker.addTo(map).bindPopup(popup);
            map.fitBounds(e.geocode.bbox);
        });

        const zoomControl = new L.Control.Zoom({ position: 'topleft' });

        if (!currentTab.mapSettings.timeSlider) {
            map.addControl(geocoderControl);
            map.addControl(zoomControl);

            // Setting in state because Leaflet Map Controls must be in a variable defined outside the
            // scope of this useEffect to be removed in following useEffect:
            setMapGeoControl(geocoderControl);
            setMapZoomControl(zoomControl);
        }
    }, [currentTab.mapSettings.timeSlider, map]);

    // Handles populating and clearing the map of control widgets and functionality.
    // Used when changing from a non-timeslider map to a timesldier map.
    // Timeslider maps sould only pan, no other functionality should be
    // allowed.
    //
    // Also handles resetting the zoom and position of the map when switching
    // from a non-time slider map (which has no zoom restrictions) to a time slider
    // map that has zoom restrictions.  Without the reset, the time slider map would be
    // locked in at whichever zoom level the previous map was at:
    useEffect(() => {
        const emptyMapControls = () => {
            if (mapGeoControl && mapZoomControl) {
                map.removeControl(mapGeoControl);
                map.removeControl(mapZoomControl);
            }

            // Disable all possible zoom options for desktop and mobile:
            map.doubleClickZoom.disable();
            map.scrollWheelZoom.disable();
            map.touchZoom.disable();
            map.keyboard.disable();

            map.whenReady(() => {
                // If we have zoomed in/out on a non-timeslider map and then switch over
                // to a time slider map while the map itself is still zoomed,
                // setting the map back to its original position with 'fitBounds' is
                // the best method to do a clean reset.
                //
                // Setting a range of `defaultMapZoom` and `defaultMapZoom - 1`
                // acts as a buffer that accounts for the potential
                // half values that are thrown during map animation events on varying
                // more compressed screen sizes:
                if (
                    map.getZoom() > defaultMapZoom ||
                    map.getZoom() < defaultMapZoom - 1
                ) {
                    map.fitBounds(stateBounds);
                } else {
                    // If we are only panning and the map is not zoomed in/out past the time slider zoom limitations,
                    // the flyTo method works better as it does not rely on map state unlike the fitBounds method:
                    const { lat, lng } = config.timeSliderMapCenter;

                    // This check is in place to limit the amount of times we call the flyTo method when switching
                    // between maps.  If the map has not moved far off of its center point, there is no need to
                    // call the 'flyTo' method:
                    if (
                        Math.round(map.getCenter().lng) !== Math.round(lng) ||
                        Math.round(map.getCenter().lat) !== Math.round(lat)
                    ) {
                        map.flyTo(new L.LatLng(lat, lng));
                    }
                }
            });
        };

        const setMapControls = () => {
            map.doubleClickZoom.enable();
            map.scrollWheelZoom.enable();
            map.touchZoom.enable();
            map.keyboard.enable();
        };

        if (currentTab.mapSettings.timeSlider) {
            emptyMapControls();
        } else {
            setMapControls();
        }
    }, [
        currentTab,
        currentTab.mapSettings.timeSlider,
        defaultMapZoom,
        map,
        mapGeoControl,
        mapZoomControl,
        stateBounds,
    ]);

    return null;
}

export default LeafletControlGeocoder;
